import type { VariablesOf } from "@graphql-typed-document-node/core";
import {
  createFileRoute,
  useRouteContext,
  useRouter,
  useSearch,
} from "@tanstack/react-router";
import { useEffect, useMemo, useState, type JSX } from "react";
import { z } from "zod";

import { cn } from "@repo/cn";
import { graphql } from "@repo/graphql-types/gql";
import CheckList from "@repo/icons/default/CheckList";
import Plus from "@repo/icons/default/Plus";
import X from "@repo/icons/default/X";

import { LatePaymentDrawer } from "@/components/payment/late-payment-drawer";
import { CardReferralBanner } from "@/components/referral/banner/card-referral-banner";
import { AsyncDataWrapper } from "@/components/ui/async-data-wrapper";
import { Fab } from "@/components/ui/fab/fab";
import { BottomBarV2 } from "@/components/ui/layout/bottom-bar/bottom-bar-v2";
import {
  HeaderIcon,
  HeaderRoot,
  HeaderTitle,
} from "@/components/ui/layout/header/header";
import { Page } from "@/components/ui/layout/page/page";
import { FullScreenModalSchedule } from "@/components/ui/modal/full-screen-modal-schedule";
import { WaitingRoomAppointmentList } from "@/components/waiting-room/waiting-room-appointment-list";
import { WaitingRoomFilterButton } from "@/components/waiting-room/waiting-room-filter-button";
import { WaitingRoomFilterDrawer } from "@/components/waiting-room/waiting-room-filter-drawer";
import { PendingBillingNotificationProvider } from "@/hooks/providers/pending-billing-notification-provider";
import { useBannerDismissed } from "@/hooks/use-banner-dismissed";
import { useBatchSelectionMode } from "@/hooks/use-batch-selection";
import { useGraphQL } from "@/hooks/use-graphql";
import { CheckinFiltersEnum } from "@/lib/enums/checkin-filters";
import { WaitingRoomStatusFiltersEnum } from "@/lib/enums/status-filters";
import { formatWithZonedDate } from "@/lib/helpers/date";
import type { InfoVariantBannerReferral } from "@/lib/launchdarkly";
import { trackEvent } from "@/lib/tracking";
import { shouldShowComponentByFF } from "@/utils/should-show-component-by-ff";

type WhereType = VariablesOf<typeof WaitingRoomPageQuery>["whereBuild"];

interface WhereCondition {
  today: string;
  tags: number | undefined;
  checkin: number | undefined;
}

function buildWhereCondition(condition: WhereCondition): WhereType {
  const whereBase: WhereType = {
    data: { _eq: condition.today },
    cancelado: { _eq: false },
    codFinalidadeAgendamento: { _nin: [2, 4] },
  };

  if (
    condition.tags === WaitingRoomStatusFiltersEnum.AppointmentUntreated &&
    condition.checkin === CheckinFiltersEnum.Pending
  ) {
    whereBase._not = { tags: {}, _or: [{ _not: { AgendamentosCheckin: {} } }] };
  } else if (condition.tags === WaitingRoomStatusFiltersEnum.AppointmentUntreated) {
    whereBase._not = { tags: {} };
  } else if (condition.checkin === CheckinFiltersEnum.Pending) {
    whereBase._not = { AgendamentosCheckin: {} };
  }

  if (condition.tags === WaitingRoomStatusFiltersEnum.AppointmentTreated) {
    whereBase.tags = { codAgendamentoTipoTag: { _eq: 1 } };
  }

  if (condition.checkin === CheckinFiltersEnum.Done) {
    whereBase.AgendamentosCheckin = { dataCheckin: {} };
  }

  return whereBase;
}

const WaitingRoomPageQuery = graphql(/* GraphQL */ `
  query WaitingRoomPageQuery($whereBuild: agendamentos_bool_exp!) {
    agendamentosDoDia: agendamentos(where: $whereBuild) {
      codAgendamento
    }
    ...WaitingRoomAppointmentListQueryFragment
    ...WaitingRoomFilterButtonFragment
  }
`);

const waitingRoomPageSearchSchema = z.object({
  action: z.string().optional().catch(""),
  appointmentId: z.number().optional().catch(0),
  checkin: z.number().optional().catch(CheckinFiltersEnum.None),
  status: z.number().optional().catch(WaitingRoomStatusFiltersEnum.None),
});

const INITIAL_REFETCH_INTERVAL = 2_000;
const DEFAULT_REFETCH_INTERVAL = 30_000;

const WaitingRoomPage = (): JSX.Element => {
  const searchParams = useSearch({ from: "/waiting-room/" });
  const router = useRouter();
  const { flags } = useRouteContext({ from: "__root__" });
  const showNewHome = useMemo(() => flags["exibe-nova-home"], [flags]);

  const { isDismissed, dismissBanner } = useBannerDismissed("referralWaitingRoomBanner");

  const {
    enabled: batchAdjustEnabled,
    isSelecting,
    toggleSelecting: toggleBatchSelecting,
  } = useBatchSelectionMode();

  const showLatePaymentDrawerIsActive = useMemo(
    () => flags["exibe-fluxo-de-reexecutar-cobranca"],
    [flags],
  );

  const { routesWithBannerReferral } = useMemo(() => {
    return flags["exibir-banner-de-gentileza-gera-gentileza"];
  }, [flags]);
  const infoJson = useMemo<InfoVariantBannerReferral>(
    () => flags["gerenciar-titulo-e-descricao-do-gentileza-gera-gentileza"],
    [flags],
  );

  const shouldShowBannerReferral = shouldShowComponentByFF(routesWithBannerReferral);

  const today = formatWithZonedDate(new Date());
  // initial value to sync after two seconds and bring patient payment if value is updated
  const [refetchInterval, setRefetchInterval] = useState(INITIAL_REFETCH_INTERVAL);

  const queryResult = useGraphQL(
    WaitingRoomPageQuery,
    {
      whereBuild: buildWhereCondition({
        today,
        tags: searchParams.status,
        checkin: searchParams.checkin,
      }),
    },
    { refetchInterval },
  );

  const trackClickedFabEvent = (): void => {
    trackEvent("Fab Clicado", {
      origem: "Sala de espera",
    });
  };

  const onClickFab = (): void => {
    trackClickedFabEvent();

    void router.navigate({
      to: ".",
      search: { action: "open-more-actions" },
    });
  };
  const onCloseModal = (): void => {
    router.history.back();
  };
  const isFullScreenModalOpen = searchParams.action === "open-more-actions";

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy code
  useEffect(() => {
    const timeout = setTimeout(() => void queryResult.refetch(), 2000);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only run once
  }, []);

  const { data, isRefetching } = queryResult;

  if (isRefetching && refetchInterval === INITIAL_REFETCH_INTERVAL) {
    setRefetchInterval(DEFAULT_REFETCH_INTERVAL);
  }

  const hasAppointments = (data?.agendamentosDoDia.length ?? 0) > 0;
  const showReferralBanner = Boolean(
    shouldShowBannerReferral && infoJson.cardBanner && !isDismissed,
  );

  return (
    <>
      <HeaderRoot className={cn(!showNewHome && "rounded-b-2xl md:rounded-b-none")}>
        <HeaderTitle title="Sala de espera" />
        {batchAdjustEnabled && (hasAppointments || isSelecting) ? (
          <HeaderIcon
            align="end"
            aria-label={
              isSelecting ? "Sair do modo de seleção" : "Selecionar agendamentos"
            }
            icon={isSelecting ? X : CheckList}
            onClick={toggleBatchSelecting}
          />
        ) : (
          <div />
        )}
        {!isSelecting && data ? (
          <WaitingRoomFilterButton data={data} align="end" />
        ) : (
          <div />
        )}
        {!showNewHome && showReferralBanner ? (
          <CardReferralBanner align="bottom" onDismiss={dismissBanner} />
        ) : (
          <div />
        )}
      </HeaderRoot>
      <Page
        className="pb-[120px]"
        sheet={showNewHome}
        sheetHeader={
          showReferralBanner ? <CardReferralBanner onDismiss={dismissBanner} /> : null
        }
      >
        <AsyncDataWrapper {...queryResult}>
          {data ? <WaitingRoomAppointmentList data={data} /> : null}
          <WaitingRoomFilterDrawer />
        </AsyncDataWrapper>

        {isSelecting ? null : (
          <Fab onClick={onClickFab}>
            <Plus size={32} className="fill-white" />
          </Fab>
        )}
        <FullScreenModalSchedule isOpen={isFullScreenModalOpen} onClose={onCloseModal} />
      </Page>
      {showLatePaymentDrawerIsActive ? (
        <PendingBillingNotificationProvider>
          <LatePaymentDrawer />
        </PendingBillingNotificationProvider>
      ) : null}
      <BottomBarV2 hidden={isSelecting} />
    </>
  );
};

export const Route = createFileRoute("/waiting-room/")({
  validateSearch: waitingRoomPageSearchSchema,
  component: WaitingRoomPage,
  beforeLoad: ({ context }) => {
    return {
      showTreatedAppointmentInWaitingRoom:
        context.ldClient.getShowTreatedAppointmentInWaitingRoom(),
      showMembersRecommendationForPatientsButtons:
        context.ldClient.getShowMembersRecommendationForPatientsButtons(),
    };
  },
  loader: ({ context }) => {
    const showTreatedAppointmentInWaitingRoom =
      context.showTreatedAppointmentInWaitingRoom;
    const showMembersRecommendationForPatientsButtons =
      context.showMembersRecommendationForPatientsButtons;

    return {
      showTreatedAppointmentInWaitingRoom,
      showMembersRecommendationForPatientsButtons,
    };
  },
});
